// cli/src/commands/mcp-server.ts
import { Command } from "commander";

/**
 * Creates the `skaile mcp-server` command.
 *
 * Starts a stdio MCP server exposing skaile workspace tools, built directly
 * on `@modelcontextprotocol/sdk` (no Claude Agent SDK). Registered in
 * `.claude/mcp.json` / `.codex/config.toml` by `skaile plugin install`.
 * The host agent spawns this process and communicates over stdio — it runs
 * on plain Node.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/plugin#make-mcp-server-command
 */
export function makeMcpServerCommand(): Command {
  return new Command("mcp-server")
    .description("Start skaile workspace MCP server (stdio transport)")
    .option("--project-dir <path>", "Project directory (default: cwd)", process.cwd())
    .action(async (opts: { projectDir: string }) => {
      const path = await import("node:path");
      const projectDir = path.default.resolve(opts.projectDir);

      // stdout is the MCP JSON-RPC channel — any log line on it corrupts the
      // protocol. Register a stderr-only LogStore before anything constructs
      // a logger (the WorkspacePlugin constructor does, on the next line).
      const { LogStore, StdoutSink, registerLogStore } = await import(
        "@skaile/workspaces/core/logging"
      );
      registerLogStore(
        new LogStore({
          sessionId: "mcp-server",
          level: "info",
          sinks: [new StdoutSink({ stream: "stderr" })],
        }),
      );

      const { WorkspacePlugin } = await import("@skaile/workspaces/workspace-plugin");
      const plugin = new WorkspacePlugin({
        projectDir,
        bootOwned: true,
        onLog: (msg) => process.stderr.write(`${msg}\n`),
      });

      await plugin.boot();

      // Register signal handlers before connecting the stdio transport.
      const shutdown = async () => {
        await plugin.shutdown();
      };
      process.on("SIGINT", async () => {
        await shutdown();
        process.exit(0);
      });
      process.on("SIGTERM", async () => {
        await shutdown();
        process.exit(0);
      });

      // Build the raw @modelcontextprotocol/sdk server and connect stdio.
      // No @anthropic-ai/claude-agent-sdk on this path — runs on Node.
      await plugin.runMcpServerStdio();
    });
}
